<div class="image-generator-container">
  <!-- LIST VIEW -->
  @if (viewMode() === 'LIST') {
    <div class="list-view">
      <div class="header list-header">
        <div class="header-titles">
          <h2>{{ 'ImageGenerator.Title' | translate }}</h2>
          <p>{{ 'ImageGenerator.ManagementSubtitle' | translate }}</p>
        </div>
        <div class="header-actions">
          <button mat-icon-button (click)="close.emit()">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      </div>
      <div class="content list-content">
        <!-- Create New Section -->
        <div class="section-block">
          <div class="create-cards">
            <!-- Create Card -->
            @if (!cannotUseAI()) {
              <div class="create-card ai-card" (click)="createNew()">
                <div class="card-icon-wrapper ai-icon-wrapper">
                  <mat-icon>auto_awesome</mat-icon>
                </div>
                <div class="card-info">
                  <h4>{{ 'ImageGenerator.CreateNew' | translate }}</h4>
                  <p>{{ 'ImageGenerator.CreateNewDesc' | translate }}</p>
                </div>
              </div>
            }
          </div>
        </div>
        <!-- Existing Drafts Section -->
        <mat-divider class="mb-3"></mat-divider>
        <div class="section-block">
          <div class="draft-list">
            <!-- Draft Items -->
            @for (draft of visibleDrafts(); track draft._id) {
              <div class="draft-item" (click)="openDraft(draft)">
                <div class="item-icon-wrapper">
                  <mat-icon>image</mat-icon>
                </div>
                <div class="item-content-row">
                  <h4 class="item-title">{{ getDraftTitle(draft) }}</h4>
                  <div class="item-subtitle">
                    <span>{{ draft.versions.length }} {{ 'ImageGenerator.Versions' | translate }}</span>
                    @if (draft.status === 'generating') {
                      <span class="text-warning">• {{ 'ImageGenerator.Generating' | translate }}</span>
                    }
                  </div>
                </div>
                <div class="item-actions-row">
                  <button #deleteBtn mat-icon-button class="delete-btn" (click)="deleteDraft(draft._id, $event)">
                    <mat-icon>delete_outline</mat-icon>
                  </button>
                </div>
              </div>
            }
            @if (visibleDrafts().length === 0) {
              <div class="empty-state">
                <p>{{ 'ImageGenerator.NoDrafts' | translate }}</p>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  }

  <!-- AI SETUP MODE -->
  @if (viewMode() === 'SETUP') {
    <div class="header">
      <h2>{{ 'ImageGenerator.CreateTitle' | translate }}</h2>
      <button mat-icon-button (click)="cancelSetup()">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  }

  @if (viewMode() === 'SETUP') {
    <div class="content ai-setup-content">
      <form [formGroup]="setupForm">
        <!-- Prompt -->
        <label class="form-label">{{ 'ImageGenerator.PromptLabel' | translate }} *</label>
        <mat-form-field appearance="fill" class="setup-field" subscriptSizing="dynamic">
          <textarea matInput formControlName="prompt" [placeholder]="'ImageGenerator.PromptPlaceholder' | translate" cdkTextareaAutosize cdkAutosizeMinRows="4" cdkAutosizeMaxRows="8"></textarea>
        </mat-form-field>
        <div class="form-row">
          <!-- Aspect Ratio -->
          <div class="field-group">
            <label class="form-label">{{ 'ImageGenerator.AspectRatioLabel' | translate }}</label>
            <mat-form-field appearance="fill" class="setup-field" subscriptSizing="dynamic">
              <mat-select formControlName="aspectRatio">
                @for (ratio of aspectRatios; track ratio.value) {
                  <mat-option [value]="ratio.value">{{ ratio.label | translate }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
          <!-- Model -->
          <div class="field-group">
            <label class="form-label">{{ 'ImageGenerator.ModelLabel' | translate }}</label>
            <mat-form-field appearance="fill" class="setup-field" subscriptSizing="dynamic">
              <mat-select formControlName="model">
                @for (model of models; track model.value) {
                  <mat-option [value]="model.value">{{ model.label | translate }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
        </div>

        <!-- Media Context Indicator -->
        @if (attachedFiles() && attachedFiles().length > 0) {
          <div class="attached-files-container mt-3">
            @for (file of attachedFiles(); track file.id) {
              <!-- Regular File Tile -->
              <div class="media-context-card mb-2 flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div class="flex items-center gap-3">
                  <div class="icon-wrapper flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-50 text-indigo-500">
                    @if (file.uploading) {
                      <mat-spinner diameter="20"></mat-spinner>
                    } @else {
                      @if (file.type === 'video') {
                        <mat-icon class="media-icon">videocam</mat-icon>
                      } @else if (file.type === 'image') {
                        <mat-icon class="media-icon">image</mat-icon>
                      } @else {
                        <mat-icon class="media-icon">description</mat-icon>
                      }
                    }
                  </div>
                  <div class="info-wrapper flex flex-col">
                    <h4 [matTooltip]="file.name" class="m-0 text-sm font-medium max-w-[200px] truncate">{{ file.name }}</h4>
                    @if (file.uploading) {
                      <p class="m-0 text-xs text-gray-500">{{ 'FileUploader.Uploading' | translate }}...</p>
                    } @else {
                      <p class="m-0 text-xs text-gray-500">{{ 'ContentGenerator.FileAttached' | translate }}</p>
                    }
                  </div>
                </div>
                <button type="button" mat-icon-button class="remove-btn" (click)="removeAttachment(file.id)">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            }
          </div>
        }

        <div class="file-actions mt-3 flex gap-2 flex-wrap mb-2">
          <button type="button" mat-stroked-button class="file-actions" (click)="fileInput.click()">
            <mat-icon>attach_file</mat-icon>
            {{ 'ContentGenerator.AttachFile' | translate }}
          </button>
          <input type="file" #fileInput hidden (change)="onFileSelected($event)" accept="image/*" />
        </div>
      </form>
    </div>
  }

  <!-- LOADING MODE -->
  @if (viewMode() === 'LOADING') {
    <div class="content ai-loading-content">
      <div class="loading-wrapper">
        <div class="pulsing-icon">
          <mat-icon>auto_awesome</mat-icon>
        </div>
        <h3>{{ 'ImageGenerator.Generating' | translate }}</h3>
        <p>{{ 'ImageGenerator.GeneratingDesc' | translate }}</p>
        <div class="spinner-dots">
          <mat-spinner diameter="24"></mat-spinner>
        </div>
      </div>
    </div>
  }

  <!-- SETUP FOOTER -->
  @if (viewMode() === 'SETUP') {
    <div class="footer">
      <button mat-flat-button color="primary" class="insert-btn" (click)="generate()" [disabled]="setupForm.invalid || !allFilesDoneUploading()">
        {{ 'ImageGenerator.GenerateAction' | translate }}
      </button>
      <div class="restart-link-wrapper">
        <a class="restart-link" (click)="cancelSetup()">{{ 'Global.Cancel' | translate }}</a>
      </div>
    </div>
  }
</div>
